import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type TabsThumbStyleConstantType,
    type TabsMarkStyleConstantType,
    type TabsMeasuresConstantType,
    type TabsColorsConstantType,
    type TabsDynamicStyleType,
    type TabsMeasures,
    type TabsColors,
    type TabsSize
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    getControlMeasures,
    getHitArea
} from "../../helpers/theme/control";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";

export const TABS_SEGMENT_PADDING_KEYS: Record<TabsSize, keyof NCoreUIKit.ControlPaddings> = {
    large: "medium",
    medium: "small",
    small: "small"
};

export const TABS_LINE_GAP_KEYS: Record<TabsSize, keyof NCoreUIKit.Spaces> = {
    medium: "spacingLg",
    large: "spacingLg",
    small: "spacingMd"
};

export const getTabsMeasures = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    touchTargets,
    orientation,
    pointerType,
    iconSizes,
    radiuses,
    variant,
    spaces,
    size
}: TabsMeasuresConstantType): TabsMeasures => {
    const currentControl = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const isSegment = variant === "segment";

    const isVertical = orientation === "vertical";

    const inset = spaces.spacingXs;

    const trackHeight = currentControl.minHeight;

    const itemHeight = isSegment ? trackHeight - inset * 2 : trackHeight;

    const trackRadius = isVertical ? Math.min(radiuses.actions + inset, radiuses.surface) : radiuses.actions + inset;

    const cellHeight = getHitArea({
        visualSize: isVertical && isSegment ? itemHeight : trackHeight,
        touchTargets,
        pointerType
    }).minHeight;

    const segmentPadding = controlPaddings[TABS_SEGMENT_PADDING_KEYS[size]];

    const isLineVertical = isVertical && !isSegment;

    return {
        paddingStart: isLineVertical ? controlPaddings.medium : isSegment ? segmentPadding : 0,
        paddingEnd: isLineVertical ? controlPaddings.small : isSegment ? segmentPadding : 0,
        lineGap: !isSegment && !isVertical ? spaces[TABS_LINE_GAP_KEYS[size]] : 0,
        reserveInset: isVertical ? 0 : (cellHeight - trackHeight) / 2,
        cellInset: !isSegment && !isVertical ? spaces.spacingSm : 0,
        labelVariant: currentControl.labelVariant,
        iconSize: currentControl.iconSize,
        itemRadius: concentric({
            outer: trackRadius,
            role: trackRadius,
            inset
        }),
        trackHeight,
        trackRadius,
        cellHeight,
        itemHeight,
        inset
    };
};

export const getTabsColors = ({
    isHighlighted,
    isDisabled,
    isSelected,
    colors
}: TabsColorsConstantType): TabsColors => {
    if(isDisabled) {
        const disabledColors = getTypeColors({
            isDisabled: true,
            variant: "ghost",
            type: "neutral",
            colors
        });

        return {
            titleColor: disabledColors.titleColor,
            iconColor: disabledColors.titleColor,
            iconColorKey: "disabled"
        };
    }

    if(isSelected || isHighlighted) {
        return {
            titleColor: colors.content.text.high,
            iconColor: colors.content.text.high,
            iconColorKey: "high"
        };
    }

    return {
        titleColor: colors.content.text.mid,
        iconColor: colors.content.text.mid,
        iconColorKey: "default"
    };
};

export const getTabsThumbStyle = ({
    shapeConfigs,
    measures,
    colors
}: TabsThumbStyleConstantType): ViewStyle => {
    return {
        backgroundColor: colors.content.container.selected,
        ...getCornerStyle({
            radius: measures.itemRadius,
            shapeConfigs
        })
    };
};

export const getTabsMarkStyle = ({
    shapeConfigs,
    orientation,
    measures,
    borders,
    variant,
    colors
}: TabsMarkStyleConstantType): ViewStyle => {
    if(variant === "segment") {
        return {
            borderColor: colors.content.border.subtle,
            borderWidth: borders.line,
            ...getCornerStyle({
                radius: measures.itemRadius,
                shapeConfigs
            })
        };
    }

    if(orientation === "vertical") {
        return {
            backgroundColor: colors.content.text.high,
            width: borders.focus
        };
    }

    return {
        backgroundColor: colors.content.text.high,
        height: borders.focus
    };
};

const stylesheet = StyleSheet.create({
    ring: {
        ...StyleSheet.absoluteFill,
        pointerEvents: "none"
    },
    bar: {
        pointerEvents: "none",
        position: "absolute",
        bottom: 0,
        start: 0,
        end: 0
    },
    barVertical: {
        pointerEvents: "none",
        position: "absolute",
        bottom: 0,
        start: 0,
        top: 0
    },
    container: {
        minWidth: 0
    }
});

export const useStyles = ({
    orientation,
    spaces
}: TabsDynamicStyleType) => {
    const isVertical = orientation === "vertical";

    const styles = {
        container: {
            gap: isVertical ? spaces.spacingXl : spaces.spacingMd,
            alignItems: isVertical ? "flex-start" : "stretch",
            flexDirection: isVertical ? "row" : "column"
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
